import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { OrangeSection } from '../../components/ui/OrangeSection'
import { QRScannerModal } from '../../components/admin/QRScannerModal'
import { QRGeneratorModal } from '../../components/admin/QRGeneratorModal'
import PrintButton from '../../components/print/PrintButton'
import {
  Package,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  MapPin,
  ArrowLeft,
  RefreshCw,
  Box,
  Hash,
  Loader2,
  Search,
  QrCode,
  Camera,
  Check,
  LayoutGrid,
  LayoutList,
  MoveVertical,
  BookOpen,
  Layers
} from 'lucide-react'

interface Tote {
  id: string
  code: string
  name: string
  description?: string
  tote_code?: string
  qr_code?: string
  location_id?: string
  location_name?: string
  shelf_id?: string
  shelf_name?: string
  tier_number?: number
  capacity?: number
  current_count: number
  item_count: number
  created_at: string
  updated_at: string
}

interface Location {
  id: string
  name: string
}

interface Shelf {
  id: string
  name: string
  tiers: number
  location_id: string
  location_name?: string
}

const API_BASE = '' // Use relative URLs for API calls (proxied by Vite)

export default function TotesPage() {
  const navigate = useNavigate()
  const [totes, setTotes] = useState<Tote[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [shelves, setShelves] = useState<Shelf[]>([])
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingTote, setEditingTote] = useState<Tote | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tote_code: '',
    qr_code: '',
    location_id: '',
    capacity: ''
  })
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card')
  const [showQRScanner, setShowQRScanner] = useState(false)
  const [showQRModal, setShowQRModal] = useState(false)
  const [selectedTote, setSelectedTote] = useState<any>(null)
  const [qrTarget, setQrTarget] = useState<'create' | 'edit' | 'search'>('create')

  // Move to Shelf state
  const [showMoveModal, setShowMoveModal] = useState(false)
  const [movingTote, setMovingTote] = useState<Tote | null>(null)
  const [moveShelfId, setMoveShelfId] = useState('')
  const [moveTierNumber, setMoveTierNumber] = useState(1)
  const [showQRShelfScanner, setShowQRShelfScanner] = useState(false)
  const [showTierInput, setShowTierInput] = useState(false)
  const [selectedShelf, setSelectedShelf] = useState<Shelf | null>(null)

  // Fetch all totes
  const fetchTotes = async () => {
    try {
      setFetching(true)
      setError(null)
      const response = await fetch(`${API_BASE}/api/totes-pg`)
      const data = await response.json()

      if (data.success) {
        setTotes(data.data)
      } else {
        setError(data.error || 'Failed to fetch totes')
      }
    } catch (err) {
      setError('Network error: Unable to fetch totes')
      console.error('Error fetching totes:', err)
    } finally {
      setFetching(false)
    }
  }

  // Fetch all locations for dropdown
  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/locations-pg`)
      const data = await response.json()

      if (data.success) {
        setLocations(data.data)
      }
    } catch (err) {
      console.error('Error fetching locations:', err)
    }
  }

  // Fetch all shelves for move modal
  const fetchShelves = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/shelves-pg`)
      const data = await response.json()

      if (data.success) {
        setShelves(data.data)
      }
    } catch (err) {
      console.error('Error fetching shelves:', err)
    }
  }

  useEffect(() => {
    fetchTotes()
    fetchLocations()
    fetchShelves()
  }, [])

  // Filter totes based on search query
  const filteredTotes = totes.filter(tote =>
    tote.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tote.tote_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tote.qr_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tote.location_name?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // QR handlers
  const handleQRScan = (result: string) => {
    setFormData({ ...formData, qr_code: result })
    setShowQRScanner(false)
  }

  const handleSearchByQR = (result: string) => {
    setSearchQuery(result)
    setShowQRScanner(false)
  }

  const handleGenerateCode = (tote: any) => {
    setSelectedTote(tote)
    setShowQRModal(true)
  }

  const handleQRGenerate = async (qrCode: string) => {
    if (!selectedTote) return

    try {
      const response = await fetch(`${API_BASE}/api/totes-pg/${selectedTote.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_code: qrCode })
      })

      if (response.ok) {
        fetchTotes()
        setShowQRModal(false)
      }
    } catch (error) {
      console.error('Error saving QR code:', error)
    }
  }

  // Move to Shelf handlers
  const openMoveModal = (tote: Tote) => {
    setMovingTote(tote)
    setMoveShelfId('')
    setMoveTierNumber(1)
    setSelectedShelf(null)
    setShowMoveModal(true)
  }

  const handleShelfQRScan = async (qrCode: string) => {
    // Parse QR: SHELF:{uuid}
    const shelfId = qrCode.replace('SHELF:', '')

    // Fetch shelf details
    try {
      const response = await fetch(`${API_BASE}/api/shelves-pg/${shelfId}`)
      const data = await response.json()

      if (data.success) {
        const shelf = data.data
        setSelectedShelf(shelf)
        setMoveShelfId(shelfId)
        setMoveTierNumber(1)
        setShowQRShelfScanner(false)
        setShowTierInput(true)
      } else {
        alert('Shelf not found')
      }
    } catch (err) {
      console.error('Error fetching shelf:', err)
      alert('Error fetching shelf')
    }
  }

  const handleManualShelfSelect = (shelfId: string) => {
    const shelf = shelves.find(s => s.id === shelfId)
    setSelectedShelf(shelf || null)
    setMoveShelfId(shelfId)
    if (shelf) {
      setMoveTierNumber(1)
    }
  }

  const handleMoveToShelf = async () => {
    if (!movingTote || !moveShelfId || !moveTierNumber) {
      alert('Please select a shelf and tier number')
      return
    }

    if (selectedShelf && moveTierNumber > selectedShelf.tiers) {
      alert(`Tier number must be between 1 and ${selectedShelf.tiers}`)
      return
    }

    try {
      const response = await fetch(`${API_BASE}/api/totes-pg/${movingTote.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shelf_id: moveShelfId,
          tier_number: moveTierNumber
        })
      })

      const data = await response.json()

      if (data.success) {
        await fetchTotes()
        setShowMoveModal(false)
        setShowTierInput(false)
        setMovingTote(null)
        alert(`Tote moved to ${selectedShelf?.name}, Tier ${moveTierNumber}`)
      } else {
        alert(data.error || 'Failed to move tote')
      }
    } catch (err) {
      console.error('Error moving tote:', err)
      alert('Error moving tote')
    }
  }

  const handleCreateTote = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      setError('Tote name is required')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${API_BASE}/api/totes-pg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          tote_code: formData.tote_code || null,
          qr_code: formData.qr_code || null,
          location_id: formData.location_id || null,
          capacity: formData.capacity ? parseInt(formData.capacity) : null
        })
      })

      const data = await response.json()

      if (data.success) {
        await fetchTotes() // Refresh the list
        setFormData({ name: '', description: '', tote_code: '', qr_code: '', location_id: '', capacity: '' })
        setShowCreateDialog(false)
      } else {
        setError(data.error || 'Failed to create tote')
      }
    } catch (err) {
      setError('Network error: Unable to create tote')
      console.error('Error creating tote:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleEditTote = (tote: Tote) => {
    setEditingTote(tote)
    setFormData({
      name: tote.name,
      description: tote.description || '',
      tote_code: tote.tote_code || '',
      qr_code: tote.qr_code || '',
      location_id: tote.location_id || '',
      capacity: tote.capacity?.toString() || ''
    })
    setError(null)
  }

  const handleUpdateTote = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!editingTote) return

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${API_BASE}/api/totes-pg/${editingTote.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          tote_code: formData.tote_code || null,
          qr_code: formData.qr_code || null,
          location_id: formData.location_id || null,
          capacity: formData.capacity ? parseInt(formData.capacity) : null
        })
      })

      const data = await response.json()

      if (data.success) {
        await fetchTotes() // Refresh the list
        setEditingTote(null)
        setFormData({ name: '', description: '', tote_code: '', qr_code: '', location_id: '', capacity: '' })
      } else {
        setError(data.error || 'Failed to update tote')
      }
    } catch (err) {
      setError('Network error: Unable to update tote')
      console.error('Error updating tote:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteTote = async (toteId: string, toteName: string) => {
    if (!confirm(`Are you sure you want to delete "${toteName}"?`)) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${API_BASE}/api/totes-pg/${toteId}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        await fetchTotes() // Refresh the list
      } else {
        setError(data.error || 'Failed to delete tote')
      }
    } catch (err) {
      setError('Network error: Unable to delete tote')
      console.error('Error deleting tote:', err)
    } finally {
      setLoading(false)
    }
  }

  const getUtilization = (tote: Tote) => {
    if (!tote.capacity) return null
    const percentage = (tote.item_count / tote.capacity) * 100
    return Math.round(percentage)
  }

  const getUtilizationColor = (percentage: number | null) => {
    if (percentage === null) return 'text-gray-400'
    if (percentage >= 90) return 'text-red-400'
    if (percentage >= 70) return 'text-yellow-400'
    return 'text-green-400'
  }

  const getUtilizationBarColor = (percentage: number | null) => {
    if (percentage === null) return 'bg-gray-500'
    if (percentage >= 90) return 'bg-gradient-to-r from-red-500 to-rose-500'
    if (percentage >= 70) return 'bg-gradient-to-r from-yellow-500 to-amber-500'
    return 'bg-gradient-to-r from-green-500 to-emerald-500'
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#FF6B35',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
            }}>
              TOTES MANAGEMENT
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '16px',
              color: '#8A95AA',
              margin: '8px 0 0 0',
              letterSpacing: '1px'
            }}>
              CONTAINER ALLOCATION PROTOCOLS
            </p>
          </div>
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center'
          }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/admin')}
              style={{
                background: 'rgba(255, 107, 53, 0.1)',
                border: '2px solid rgba(255, 107, 53, 0.3)',
                color: '#FF6B35',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: 600,
                letterSpacing: '1px'
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              BACK
            </Button>
            <Button
              onClick={fetchTotes}
              disabled={fetching}
              style={{
                background: 'rgba(255, 107, 53, 0.1)',
                border: '2px solid rgba(255, 107, 53, 0.3)',
                color: '#FF6B35',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: 600,
                letterSpacing: '1px'
              }}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${fetching ? 'animate-spin' : ''}`} />
              REFRESH
            </Button>
            <Button
              onClick={() => setShowCreateDialog(true)}
              style={{
                background: 'rgba(255, 107, 53, 0.2)',
                border: '2px solid #FF6B35',
                color: '#FF6B35',
                fontFamily: 'Rajdhani, sans-serif',
                fontWeight: 700,
                letterSpacing: '1px',
                boxShadow: '0 0 20px rgba(255, 107, 53, 0.3)'
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              NEW TOTE
            </Button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg flex items-center justify-between mt-6">
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Search & View Toggle */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        alignItems: 'center'
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '16px',
            height: '16px',
            color: '#8A95AA'
          }} />
          <input
            type="text"
            placeholder="SEARCH TOTES..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              background: 'rgba(26, 31, 46, 0.6)',
              border: '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: '8px',
              color: '#E0E6F0',
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '1px',
              outline: 'none',
              transition: 'all 0.3s'
            }}
            onFocus={(e) => {
              e.target.style.border = '2px solid rgba(255, 107, 53, 0.6)'
              e.target.style.boxShadow = '0 0 20px rgba(255, 107, 53, 0.2)'
            }}
            onBlur={(e) => {
              e.target.style.border = '2px solid rgba(255, 107, 53, 0.3)'
              e.target.style.boxShadow = 'none'
            }}
          />
        </div>
        <Button
          onClick={() => { setQrTarget('search'); setShowQRScanner(true); }}
          style={{
            background: 'rgba(255, 107, 53, 0.1)',
            border: '2px solid rgba(255, 107, 53, 0.3)',
            color: '#FF6B35',
            padding: '12px',
            borderRadius: '8px'
          }}
        >
          <QrCode className="h-5 w-5" />
        </Button>
        <div style={{
          display: 'flex',
          gap: '4px',
          background: 'rgba(26, 31, 46, 0.6)',
          padding: '4px',
          borderRadius: '8px',
          border: '2px solid rgba(255, 107, 53, 0.3)'
        }}>
          <Button
            onClick={() => setViewMode('card')}
            style={{
              background: viewMode === 'card' ? 'rgba(255, 107, 53, 0.2)' : 'transparent',
              border: viewMode === 'card' ? '1px solid #FF6B35' : '1px solid transparent',
              color: viewMode === 'card' ? '#FF6B35' : '#8A95AA',
              padding: '8px 12px',
              borderRadius: '4px'
            }}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            onClick={() => setViewMode('table')}
            style={{
              background: viewMode === 'table' ? 'rgba(255, 107, 53, 0.2)' : 'transparent',
              border: viewMode === 'table' ? '1px solid #FF6B35' : '1px solid transparent',
              color: viewMode === 'table' ? '#FF6B35' : '#8A95AA',
              padding: '8px 12px',
              borderRadius: '4px'
            }}
          >
            <LayoutList className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Create Dialog */}
      {showCreateDialog && (
        <Card className="bg-black/60 backdrop-blur-md border-2 border-purple-500/30 shadow-2xl shadow-purple-500/20 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-400">
              <Plus className="h-5 w-5" />
              CREATE NEW TOTE
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateTote} className="space-y-4">
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Tote A1"
                  required
                  className="bg-black/40 border-purple-500/30"
                />
              </div>
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="General storage tote"
                  className="bg-black/40 border-purple-500/30"
                />
              </div>
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">Tote Code</label>
                <Input
                  value={formData.tote_code}
                  onChange={(e) => setFormData({ ...formData, tote_code: e.target.value })}
                  placeholder="TOTE-001"
                  className="bg-black/40 border-purple-500/30"
                />
              </div>
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">QR Code</label>
                <div className="flex gap-2">
                  <Input
                    value={formData.qr_code}
                    onChange={(e) => setFormData({ ...formData, qr_code: e.target.value })}
                    placeholder="Scan or enter QR code"
                    className="flex-1 bg-black/40 border-purple-500/30"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => { setQrTarget('create'); setShowQRScanner(true); }}
                    className="hover:bg-purple-500/20 border-purple-500/30"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => { setQrTarget('create'); setShowQRGenerator(true); }}
                    disabled={true}
                    title="Generate QR is only available after creating the tote"
                    className="hover:bg-purple-500/20 border-purple-500/30"
                  >
                    <QrCode className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">Location</label>
                <select
                  value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                  className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-content"
                >
                  <option value="">No location</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">Capacity</label>
                <Input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  placeholder="Maximum items"
                  min="0"
                  className="bg-black/40 border-purple-500/30"
                />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-purple-500/20 hover:bg-purple-500/30 border-purple-500/50"
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Create
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowCreateDialog(false)
                    setFormData({ name: '', description: '', tote_code: '', qr_code: '', location_id: '', capacity: '' })
                    setError(null)
                  }}
                  className="hover:bg-purple-500/20 border-purple-500/30"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      {editingTote && (
        <Card className="bg-black/60 backdrop-blur-md border-2 border-purple-500/30 shadow-2xl shadow-purple-500/20 mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-400">
              <Edit className="h-5 w-5" />
              EDIT TOTE
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateTote} className="space-y-4">
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-black/40 border-purple-500/30"
                />
              </div>
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-black/40 border-purple-500/30"
                />
              </div>
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">Tote Code</label>
                <Input
                  value={formData.tote_code}
                  onChange={(e) => setFormData({ ...formData, tote_code: e.target.value })}
                  className="bg-black/40 border-purple-500/30"
                />
              </div>
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">QR Code</label>
                <div className="flex gap-2">
                  <Input
                    value={formData.qr_code}
                    onChange={(e) => setFormData({ ...formData, qr_code: e.target.value })}
                    className="flex-1 bg-black/40 border-purple-500/30"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => { setQrTarget('edit'); setShowQRScanner(true); }}
                    className="hover:bg-purple-500/20 border-purple-500/30"
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => { setQrTarget('edit'); setShowQRGenerator(true); }}
                    disabled={!editingTote?.id}
                    className="hover:bg-purple-500/20 border-purple-500/30"
                  >
                    <QrCode className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">Location</label>
                <select
                  value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                  className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-content"
                >
                  <option value="">No location</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-white mb-1 block uppercase tracking-wide">Capacity</label>
                <Input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  min="0"
                  className="bg-black/40 border-purple-500/30"
                />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-purple-500/20 hover:bg-purple-500/30 border-purple-500/50"
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Update
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setEditingTote(null)
                    setFormData({ name: '', description: '', tote_code: '', qr_code: '', location_id: '', capacity: '' })
                    setError(null)
                  }}
                  className="hover:bg-purple-500/20 border-purple-500/30"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Totes List */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '8px',
            background: 'rgba(185, 27, 255, 0.2)',
            border: '2px solid #B91BFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Package style={{ width: 24, height: 24, color: '#B91BFF' }} />
          </div>
          <h2 style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '24px',
            fontWeight: 700,
            color: '#E0E6F0',
            margin: 0,
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            STORAGE TOTES ({filteredTotes.length})
          </h2>
        </div>

        {fetching ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
            </div>
          ) : filteredTotes.length === 0 ? (
            <div className="text-center py-12 text-white">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No totes found. {searchQuery ? 'Try a different search.' : 'Create your first tote to get started.'}</p>
            </div>
          ) : viewMode === 'card' ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
              gap: '20px'
            }}>
              {filteredTotes.map((tote) => {
                const utilization = getUtilization(tote)
                const toteColor = '#B91BFF'

                return (
                  <div
                    key={tote.id}
                    style={{
                      background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95), rgba(36, 41, 56, 0.95))',
                      border: `2px solid ${toteColor}`,
                      borderRadius: '12px',
                      padding: '20px',
                      position: 'relative',
                      transition: 'all 0.3s',
                      cursor: 'pointer',
                      boxShadow: `0 0 20px ${toteColor}20`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = `0 0 30px ${toteColor}40`
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = `0 0 20px ${toteColor}20`
                    }}
                  >

                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '20px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '8px',
                          background: `${toteColor}20`,
                          border: `2px solid ${toteColor}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Package style={{ width: 24, height: 24, color: toteColor }} />
                        </div>
                        <div>
                          <h3 style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '20px',
                            fontWeight: 700,
                            color: '#E0E6F0',
                            margin: 0,
                            textTransform: 'uppercase',
                            letterSpacing: '1px'
                          }}>
                            {tote.name}
                          </h3>
                          {tote.description && (
                            <p style={{
                              fontFamily: 'Rajdhani, sans-serif',
                              fontSize: '13px',
                              color: '#8A95AA',
                              margin: '4px 0 0 0'
                            }}>
                              {tote.description}
                            </p>
                          )}
                        </div>
                      </div>
                      {tote.qr_code ? (
                        <div style={{
                          background: 'rgba(16, 185, 129, 0.2)',
                          border: '1px solid #10B981',
                          borderRadius: '6px',
                          padding: '4px 8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <Check style={{ width: 12, height: 12, color: '#10B981' }} />
                          <span style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#10B981',
                            letterSpacing: '1px'
                          }}>QR</span>
                        </div>
                      ) : (
                        <div style={{
                          background: 'rgba(138, 149, 170, 0.1)',
                          border: '1px solid rgba(138, 149, 170, 0.3)',
                          borderRadius: '6px',
                          padding: '4px 8px'
                        }}>
                          <span style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#8A95AA',
                            letterSpacing: '1px'
                          }}>No QR</span>
                        </div>
                      )}
                    </div>

                    {/* Stats Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      {tote.tote_code && (
                        <div style={{
                          background: 'rgba(255, 107, 53, 0.1)',
                          border: '1px solid rgba(255, 107, 53, 0.3)',
                          borderRadius: '6px',
                          padding: '8px'
                        }}>
                          <div style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '10px',
                            color: '#8A95AA',
                            marginBottom: '4px',
                            letterSpacing: '1px',
                            textTransform: 'uppercase'
                          }}>CODE</div>
                          <div style={{
                            fontFamily: 'Rajdhani, monospace',
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#FF6B35'
                          }}>{tote.tote_code}</div>
                        </div>
                      )}
                      {tote.location_name && (
                        <div style={{
                          background: 'rgba(0, 240, 255, 0.1)',
                          border: '1px solid rgba(0, 240, 255, 0.3)',
                          borderRadius: '6px',
                          padding: '8px'
                        }}>
                          <div style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '10px',
                            color: '#8A95AA',
                            marginBottom: '4px',
                            letterSpacing: '1px',
                            textTransform: 'uppercase'
                          }}>LOCATION</div>
                          <div style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#00F0FF'
                          }}>{tote.location_name}</div>
                        </div>
                      )}
                      {tote.shelf_name && (
                        <div style={{
                          background: 'rgba(107, 207, 127, 0.1)',
                          border: '1px solid rgba(107, 207, 127, 0.3)',
                          borderRadius: '6px',
                          padding: '8px'
                        }}>
                          <div style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '10px',
                            color: '#8A95AA',
                            marginBottom: '4px',
                            letterSpacing: '1px',
                            textTransform: 'uppercase'
                          }}>SHELF</div>
                          <div style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#6BCF7F'
                          }}>{tote.shelf_name}{tote.tier_number ? ` T${tote.tier_number}` : ''}</div>
                        </div>
                      )}
                      <div style={{
                        background: 'rgba(255, 217, 61, 0.1)',
                        border: '1px solid rgba(255, 217, 61, 0.3)',
                        borderRadius: '6px',
                        padding: '8px'
                      }}>
                        <div style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '10px',
                          color: '#8A95AA',
                          marginBottom: '4px',
                          letterSpacing: '1px',
                          textTransform: 'uppercase'
                        }}>ITEMS</div>
                        <div style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '18px',
                          fontWeight: 700,
                          color: '#FFD93D'
                        }}>{tote.item_count}</div>
                      </div>
                      {tote.capacity && (
                        <div style={{
                          background: 'rgba(255, 20, 147, 0.1)',
                          border: '1px solid rgba(255, 20, 147, 0.3)',
                          borderRadius: '6px',
                          padding: '8px'
                        }}>
                          <div style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '10px',
                            color: '#8A95AA',
                            marginBottom: '4px',
                            letterSpacing: '1px',
                            textTransform: 'uppercase'
                          }}>CAPACITY</div>
                          <div style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '18px',
                            fontWeight: 700,
                            color: '#FF1493'
                          }}>{tote.capacity}</div>
                        </div>
                      )}
                      {utilization !== null && (
                        <div style={{
                          background: 'rgba(185, 27, 255, 0.1)',
                          border: '1px solid rgba(185, 27, 255, 0.3)',
                          borderRadius: '6px',
                          padding: '8px',
                          gridColumn: tote.capacity ? 'auto' : 'span 2'
                        }}>
                          <div style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '10px',
                            color: '#8A95AA',
                            marginBottom: '4px',
                            letterSpacing: '1px',
                            textTransform: 'uppercase'
                          }}>UTILIZATION</div>
                          <div style={{
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '18px',
                            fontWeight: 700,
                            color: utilization >= 90 ? '#FF1493' : utilization >= 70 ? '#FFD93D' : '#6BCF7F'
                          }}>{utilization}%</div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr auto auto auto',
                      gap: '8px',
                      marginTop: '16px',
                      paddingTop: '16px',
                      borderTop: `1px solid ${toteColor}30`
                    }}>
                      <button
                        onClick={() => openMoveModal(tote)}
                        disabled={loading}
                        style={{
                          background: 'rgba(0, 240, 255, 0.1)',
                          border: '2px solid rgba(0, 240, 255, 0.4)',
                          borderRadius: '6px',
                          padding: '10px',
                          color: '#00F0FF',
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '12px',
                          fontWeight: 700,
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(0, 240, 255, 0.2)'
                          e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.3)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <MoveVertical style={{ width: 14, height: 14 }} />
                        Move
                      </button>
                      <button
                        onClick={() => navigate(`/search?tote_id=${tote.id}`)}
                        disabled={loading}
                        style={{
                          background: 'rgba(185, 27, 255, 0.1)',
                          border: '2px solid rgba(185, 27, 255, 0.4)',
                          borderRadius: '6px',
                          padding: '10px',
                          color: '#B91BFF',
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '12px',
                          fontWeight: 700,
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(185, 27, 255, 0.2)'
                          e.currentTarget.style.boxShadow = '0 0 15px rgba(185, 27, 255, 0.3)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(185, 27, 255, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <Search style={{ width: 14, height: 14 }} />
                        Items
                      </button>
                      <button
                        onClick={() => handleEditTote(tote)}
                        disabled={loading}
                        style={{
                          background: 'rgba(255, 217, 61, 0.1)',
                          border: '2px solid rgba(255, 217, 61, 0.4)',
                          borderRadius: '6px',
                          padding: '10px',
                          color: '#FFD93D',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 217, 61, 0.2)'
                          e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 217, 61, 0.3)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 217, 61, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <Edit style={{ width: 16, height: 16 }} />
                      </button>
                      <PrintButton entityType="tote" entityData={tote} variant="icon" />
                      <button
                        onClick={() => handleGenerateCode(tote)}
                        disabled={loading}
                        style={{
                          background: 'rgba(0, 240, 255, 0.1)',
                          border: '2px solid rgba(0, 240, 255, 0.4)',
                          borderRadius: '6px',
                          padding: '10px',
                          color: '#00F0FF',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s'
                        }}
                        title="Generate QR Code"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(0, 240, 255, 0.2)'
                          e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.3)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <QrCode style={{ width: 16, height: 16 }} />
                      </button>
                      <button
                        onClick={() => handleDeleteTote(tote.id, tote.name)}
                        disabled={loading}
                        style={{
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '2px solid rgba(239, 68, 68, 0.4)',
                          borderRadius: '6px',
                          padding: '10px',
                          color: '#EF4444',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
                          e.currentTarget.style.boxShadow = '0 0 15px rgba(239, 68, 68, 0.3)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <Trash2 style={{ width: 16, height: 16 }} />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="bg-black/60 backdrop-blur-md border border-purple-500/30 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-purple-900/30 border-b border-purple-500/30">
                  <tr>
                    <th className="text-left p-3 text-purple-200 uppercase text-xs tracking-wide">Name</th>
                    <th className="text-left p-3 text-purple-200 uppercase text-xs tracking-wide">Code</th>
                    <th className="text-left p-3 text-purple-200 uppercase text-xs tracking-wide">Location</th>
                    <th className="text-left p-3 text-purple-200 uppercase text-xs tracking-wide">Shelf</th>
                    <th className="text-center p-3 text-purple-200 uppercase text-xs tracking-wide">QR</th>
                    <th className="text-center p-3 text-purple-200 uppercase text-xs tracking-wide">Items</th>
                    <th className="text-center p-3 text-purple-200 uppercase text-xs tracking-wide">Capacity</th>
                    <th className="text-right p-3 text-purple-200 uppercase text-xs tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTotes.map((tote) => (
                    <tr key={tote.id} className="border-b border-purple-500/10 hover:bg-purple-500/10 transition-colors duration-200">
                      <td className="p-3 text-purple-200 font-medium">{tote.name}</td>
                      <td className="p-3 text-orange-400 font-mono text-sm">{tote.tote_code || '-'}</td>
                      <td className="p-3 text-purple-300">{tote.location_name || '-'}</td>
                      <td className="p-3 text-white text-sm">
                        {tote.shelf_name ? `${tote.shelf_name}${tote.tier_number ? ` T${tote.tier_number}` : ''}` : '-'}
                      </td>
                      <td className="p-3 text-center">
                        {tote.qr_code ? (
                          <Check className="h-4 w-4 inline text-green-400" />
                        ) : (
                          <X className="h-4 w-4 inline text-gray-500" />
                        )}
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="secondary" className="bg-purple-500/20 text-purple-200 border border-purple-500/30">
                          {tote.item_count}
                        </Badge>
                      </td>
                      <td className="p-3 text-center text-purple-200">{tote.capacity || '-'}</td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="secondary"
                            className="hover:bg-cyan-500/20 border-cyan-500/30"
                            onClick={() => openMoveModal(tote)}
                          >
                            <MoveVertical className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="hover:bg-purple-500/20 border-purple-500/30"
                            onClick={() => navigate(`/search?tote_id=${tote.id}`)}
                          >
                            <Search className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="hover:bg-purple-500/20 border-purple-500/30"
                            onClick={() => handleEditTote(tote)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <PrintButton entityType="tote" entityData={tote} variant="icon" />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleGenerateCode(tote)}
                          >
                            <QrCode className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="hover:bg-red-500/20 hover:border-red-500/50"
                            onClick={() => handleDeleteTote(tote.id, tote.name)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      {/* QR Scanner Modal */}
      <QRScannerModal
        isOpen={showQRScanner}
        onClose={() => setShowQRScanner(false)}
        onScan={qrTarget === 'search' ? handleSearchByQR : handleQRScan}
        title={qrTarget === 'search' ? "Scan to Search" : "Scan QR Code for Tote"}
      />

      {/* QR Generator Modal */}
      {showQRModal && selectedTote && (
        <QRGeneratorModal
          isOpen={showQRModal}
          onClose={() => setShowQRModal(false)}
          onGenerate={handleQRGenerate}
          type="TOTE"
          id={selectedTote.id}
          code={selectedTote.tote_code}
          title={`Generate QR Code - ${selectedTote.name}`}
        />
      )}

      {/* Move to Shelf Modal */}
      {showMoveModal && movingTote && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 border-2 border-cyan-500/50 rounded-xl p-6 max-w-md w-full shadow-2xl shadow-cyan-500/30">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
                Move Tote to Shelf
              </h2>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowMoveModal(false)}
                className="hover:bg-red-500/20"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="space-y-4">
              <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                <p className="text-sm text-cyan-300 font-medium">Moving Tote:</p>
                <p className="text-white font-semibold">{movingTote.name}</p>
                {movingTote.tote_code && (
                  <p className="text-sm text-cyan-400 font-mono">{movingTote.tote_code}</p>
                )}
              </div>

              {!selectedShelf && !showTierInput && (
                <div className="space-y-3">
                  <p className="text-sm text-cyan-300 font-medium">Choose a method:</p>

                  <Button
                    onClick={() => setShowQRShelfScanner(true)}
                    className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Scan Shelf QR Code
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-cyan-500/30" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-black px-2 text-cyan-500">Or</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-cyan-300 mb-2">Select Shelf Manually:</label>
                    <select
                      value={moveShelfId}
                      onChange={(e) => handleManualShelfSelect(e.target.value)}
                      className="w-full p-2 bg-black/60 border border-cyan-500/30 rounded-lg text-white focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/50"
                    >
                      <option value="">-- Select a shelf --</option>
                      {shelves.map((shelf) => (
                        <option key={shelf.id} value={shelf.id}>
                          {shelf.name} - {shelf.location_name || 'No Location'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {selectedShelf && (
                <div className="space-y-3">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-sm text-blue-300 font-medium">Selected Shelf:</p>
                    <p className="text-white font-semibold">{selectedShelf.name}</p>
                    <p className="text-sm text-blue-400">{selectedShelf.location_name || 'No Location'}</p>
                    <p className="text-sm text-blue-300">Tiers: {selectedShelf.tiers}</p>
                  </div>

                  <div>
                    <label className="block text-sm text-cyan-300 mb-2">Select Tier Number:</label>
                    <input
                      type="number"
                      min="1"
                      max={selectedShelf.tiers}
                      value={moveTierNumber}
                      onChange={(e) => setMoveTierNumber(parseInt(e.target.value) || 1)}
                      className="w-full p-2 bg-black/60 border border-cyan-500/30 rounded-lg text-white focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/50"
                    />
                    <p className="text-xs text-cyan-400 mt-1">Enter a tier between 1 and {selectedShelf.tiers}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        setSelectedShelf(null)
                        setMoveShelfId('')
                        setShowTierInput(false)
                      }}
                      variant="secondary"
                      className="flex-1"
                    >
                      Back
                    </Button>
                    <Button
                      onClick={handleMoveToShelf}
                      className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500"
                      disabled={loading || !moveTierNumber || moveTierNumber < 1 || moveTierNumber > selectedShelf.tiers}
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                      Confirm Move
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* QR Scanner for Shelf Selection */}
      <QRScannerModal
        isOpen={showQRShelfScanner}
        onClose={() => setShowQRShelfScanner(false)}
        onScan={handleShelfQRScan}
        title="Scan Shelf QR Code"
      />
    </div>
  )
}
